import React, { useMemo, useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { API_ENDPOINTS, buildApiUrl } from '../../config/apiConfig';
import AdminHubPageFrame from '../../components/admin/AdminHubPageFrame';
import AdminFilterBar from '../../components/admin/AdminFilterBar';
import AdminSectionHeader from '../../components/admin/AdminSectionHeader';
import AdminMetricCard from '../../components/admin/AdminMetricCard';
import AdminStatusBadge from '../../components/admin/AdminStatusBadge';
import AdminJsonDetails from '../../components/admin/AdminJsonDetails';
import { defaultUtcRangeDays } from '../../admin/adminHubUtils';

function n(v) {
  const x = Number(v);
  return Number.isFinite(x) ? x : 0;
}

function heurTone(h) {
  if (h === 'healthy' || h === 'normal') return 'ok';
  if (h === 'watch' || h === 'notice' || h === 'elevated') return 'warn';
  if (h === 'degraded' || h === 'heavy') return 'danger';
  return 'neutral';
}

export default function AdminOperationsHealthPage() {
  const { t } = useLanguage();
  const def = defaultUtcRangeDays(14);
  const [from, setFrom] = useState(def.from);
  const [to, setTo] = useState(def.to);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    setErr(false);
    try {
      const q = new URLSearchParams({ from: from.trim(), to: to.trim() });
      const res = await fetch(`${buildApiUrl(API_ENDPOINTS.ADMIN_OPERATIONAL_HEALTH)}?${q}`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error('bad');
      setData(await res.json());
    } catch {
      setErr(true);
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  const health = data?.health;
  const totals = health?.totals || {};
  const heur = health?.heuristics || {};

  const crumbs = useMemo(
    () => [
      { label: t('adminHub.navOperations'), to: '/admin/operations/operational-health' },
      { label: t('adminHub.navOperationalHealth') },
    ],
    [t]
  );

  return (
    <AdminHubPageFrame
      titleKey="adminHub.opHealthTitle"
      descKey="adminHub.opHealthDesc"
      breadcrumbItems={crumbs}
    >
      <section className="admin-section-card">
        <AdminFilterBar>
          <label>
            {t('adminHub.dateFrom')}
            <input value={from} onChange={(e) => setFrom(e.target.value)} />
          </label>
          <label>
            {t('adminHub.dateTo')}
            <input value={to} onChange={(e) => setTo(e.target.value)} />
          </label>
          <button type="button" className="admin-hub-btn" onClick={load} disabled={loading}>
            {loading ? t('adminPage.loading') : t('adminHub.loadAction')}
          </button>
        </AdminFilterBar>
        {err ? (
          <div className="admin-state admin-state--error" role="status">
            <p>{t('adminHub.loadError')}</p>
          </div>
        ) : null}
        {data ? (
          <div className="admin-hub-kit-stack">
            <AdminSectionHeader title={t('adminKit.opsWindowTitle')} />
            <div className="admin-hub-kit-metric-grid">
              <AdminMetricCard label={t('adminKit.colDateFrom')} value={data.window?.from || '—'} />
              <AdminMetricCard label={t('adminKit.colDateTo')} value={data.window?.to || '—'} />
            </div>
            <AdminSectionHeader title={t('adminKit.opsHealthTotalsTitle')} level={3} />
            <div className="admin-hub-kit-metric-grid">
              <AdminMetricCard label={t('adminKit.metricEventCount')} value={n(totals.event_count)} />
              <AdminMetricCard label={t('adminKit.metricUsageUnits')} value={n(totals.usage_units_sum)} />
              <AdminMetricCard label={t('adminKit.metricGuestEvents')} value={n(totals.guest_events)} />
              <AdminMetricCard label={t('adminKit.metricGuestUnits')} value={n(totals.guest_units)} />
            </div>
            <AdminSectionHeader title={t('adminKit.opsHeuristicsTitle')} level={3} />
            <div className="admin-hub-kit-metric-grid">
              <AdminMetricCard
                label={t('adminKit.heurAiHealth')}
                value={<AdminStatusBadge tone={heurTone(heur.ai_usage_health)}>{heur.ai_usage_health || '—'}</AdminStatusBadge>}
              />
              <AdminMetricCard
                label={t('adminKit.heurOcrLoad')}
                value={<AdminStatusBadge tone={heurTone(heur.ocr_load)}>{heur.ocr_load || '—'}</AdminStatusBadge>}
              />
              <AdminMetricCard
                label={t('adminKit.heurLlmPressure')}
                value={<AdminStatusBadge tone={heurTone(heur.llm_heavy_operations)}>{heur.llm_heavy_operations || '—'}</AdminStatusBadge>}
              />
              <AdminMetricCard label={t('adminKit.heurFailedRatio')} value={heur.failed_analysis_ratio ?? '—'} />
            </div>
            <AdminJsonDetails title={t('adminKit.rawJsonToggle')} data={data} />
          </div>
        ) : null}
      </section>
    </AdminHubPageFrame>
  );
}
